import { connectorCatalogItemId } from "@/types/connectorCapabilities.ts";
import type { ConnectorCatalogItem, ConnectorSummary } from "@/types/connectors.ts";
import { connectorConnectMode } from "./connectMode.ts";
import { CALENDAR_PROVIDER_NAMES } from "./providers.ts";

/**
 * Framing for the connector picker card: which services to consider (`scopeIds`,
 * empty = all) and which to surface first (`pinnedIds`, in order). Both come off
 * the picker artifact's target.
 *
 * `preferredIds` comes off the target too and is no longer read — the rest of
 * the list sorts by name. Kept on the type only so the field can be dropped from
 * the artifact in its own change: the deployed clients still pass it, and web
 * and native ship independently of the server that writes it.
 */
export type ConnectorPickerFraming = {
  scopeIds: string[];
  pinnedIds: string[];
  preferredIds: string[];
};

/**
 * Split the live catalog into the picker's two sections: pinned-first (in the
 * asked-for order), then everything else by name.
 *
 * Shared by web's `ConnectorPickerBlock` and native's `ConnectorsPickerBody` so
 * the ordering can't drift. Pure — the framing decides order, the catalog is
 * read live by each caller (a connect there flips a row to "Connected" in place).
 *
 * The rest used to sort by the server's `preferredIds` ranking, which is computed
 * over what was *connectable* — so anything already connected was absent from it,
 * fell to the tiebreak, and sank to the bottom, away from the name the user was
 * looking for. Connecting a service moved it out from under them. A list someone
 * scans by name sorts by name; what is connected is the row's badge to say, not
 * its position (BIG-7231).
 *
 * A pinned or scoped id the catalog no longer has is skipped rather than leaving
 * a gap.
 */
export const connectorPickerSections = (
  catalog: ConnectorCatalogItem[],
  { scopeIds, pinnedIds }: ConnectorPickerFraming,
): { pinned: ConnectorCatalogItem[]; rest: ConnectorCatalogItem[] } => {
  const scope = new Set(scopeIds);
  const scoped = scope.size > 0 ? catalog.filter((item) => scope.has(item.id)) : catalog;

  const byId = new Map(scoped.map((item) => [item.id, item]));
  const pinned = pinnedIds.flatMap((id) => byId.get(id) ?? []);
  const pinnedSet = new Set(pinned.map((item) => item.id));

  const rest = scoped
    .filter((item) => !pinnedSet.has(item.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { pinned, rest };
};

/** Case-insensitive name/description match for the picker's search box. */
export const matchesConnectorSearch = (item: ConnectorCatalogItem, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return item.name.toLowerCase().includes(q) || item.description.toLowerCase().includes(q);
};

/**
 * What a picker row calls one catalog item.
 *
 * Calendar-variant items are the one case where the catalog name reads wrong
 * next to its icon ("Google" beside the Calendar mark), so they take the shared
 * calendar-product title. Every other variant keeps the catalog name — brand
 * titles would rename "Google Drive" to "Google", and mail titles would collide
 * "Outlook inbox" with "Outlook".
 *
 * Here rather than in either picker because both the guided flow's service list
 * and the omnibox card's rows draw the same mark beside the same name, and a
 * title that disagreed between them would be the kind of drift a user notices
 * without being able to name.
 */
export const connectorDisplayTitle = (item: ConnectorCatalogItem): string => {
  if (item.provider && item.iconVariant === "calendar") {
    return CALENDAR_PROVIDER_NAMES[item.provider] ?? item.name;
  }
  return item.name;
};

/**
 * One entry of a picker's Connected section: an account connection when the
 * provider reports identities, else the connected service itself (a dynamic
 * connector has no account row to stand in for it).
 */
export type ConnectorConnectedEntry = {
  item: ConnectorCatalogItem;
  connector?: ConnectorSummary;
};

/**
 * The user's connections grouped under the catalog id of their exact role —
 * a Gmail-inbox connection must not surface under the calendar item. Login
 * accounts first, then by email, so the main account leads every group. A
 * connection whose id matches no catalog item on a given card simply never
 * gets looked up.
 */
export const connectorAccountsByCatalogId = (
  connectors: ConnectorSummary[],
): Map<string, ConnectorSummary[]> => {
  const byId = new Map<string, ConnectorSummary[]>();
  const sorted = [...connectors].sort((a, b) => {
    if (a.isLogin !== b.isLogin) return a.isLogin ? -1 : 1;
    return (a.email ?? "").localeCompare(b.email ?? "");
  });
  for (const connector of sorted) {
    // A Connected row offers no way to connect, so a connector that does not
    // serve the item must not fill its row: a login-only Google connector under
    // Google Calendar told the user it was connected, with no way to connect it.
    // Absent is a server older than the flag, and reads as serving.
    if (connector.servesCatalogItem === false) continue;
    const id = connectorCatalogItemId(connector);
    byId.set(id, [...(byId.get(id) ?? []), connector]);
  }
  return byId;
};

/**
 * The picker's three sections: Recommended, Connected, Additional. The
 * Connected section owns connected state — every account entry lands there,
 * and a service whose action has nothing left to offer moves there wholesale.
 * What stays in the two connect sections is exactly the items still offering
 * an action, so "Connected" and "Connect" never share a row.
 */
export const connectorThreeSections = (
  pinned: ConnectorCatalogItem[],
  rest: ConnectorCatalogItem[],
  accountsByCatalogId: Map<string, ConnectorSummary[]>,
): {
  recommended: ConnectorCatalogItem[];
  connected: ConnectorConnectedEntry[];
  additional: ConnectorCatalogItem[];
} => {
  const connected: ConnectorConnectedEntry[] = [];
  const stillOffering = (items: ConnectorCatalogItem[]) =>
    items.filter((item) => {
      const accounts = accountsByCatalogId.get(item.id) ?? [];
      for (const connector of accounts) connected.push({ item, connector });
      const { hidden } = connectorConnectMode(item);
      if (accounts.length === 0 && item.connected && hidden) {
        connected.push({ item });
        return false;
      }
      return accounts.length === 0 || !hidden;
    });
  const recommended = stillOffering(pinned);
  const additional = stillOffering(rest);
  return { recommended, connected, additional };
};

/**
 * The picker's sections for a surface with no Connected section, such as the
 * connector prompt: every item stays where it is and shows its connected state
 * in place, so connecting one flips it rather than moving it out of sight.
 *
 * An item reads connected when the catalog says so, when an account fills it,
 * or, for a mailbox, when the provider's connector has mail. The mailbox items
 * are for adding another inbox and never read connected in the catalog, so a
 * user who just added mail to the account they signed in with would otherwise
 * still be asked to connect it.
 */
export const connectorSectionsInPlace = (
  pinned: ConnectorCatalogItem[],
  rest: ConnectorCatalogItem[],
  accountsByCatalogId: Map<string, ConnectorSummary[]>,
  connectors: readonly ConnectorSummary[],
): { recommended: ConnectorCatalogItem[]; additional: ConnectorCatalogItem[] } => {
  const hasMail = (provider: string | null) =>
    connectors.some((c) => c.provider === provider && c.supportsEmail && !c.needsAuth);
  const inPlace = (item: ConnectorCatalogItem): ConnectorCatalogItem => ({
    ...item,
    connected:
      item.connected ||
      (accountsByCatalogId.get(item.id)?.length ?? 0) > 0 ||
      (item.id.startsWith("email_inbox:") && hasMail(item.provider)),
  });
  return { recommended: pinned.map(inPlace), additional: rest.map(inPlace) };
};

/** The search box's match for a Connected entry: the service, or the
 *  account's email — accounts are findable by who they are. */
export const matchesConnectedEntrySearch = (
  entry: ConnectorConnectedEntry,
  query: string,
): boolean =>
  matchesConnectorSearch(entry.item, query) ||
  (entry.connector?.email ?? "").toLowerCase().includes(query.trim().toLowerCase());
